import { useQuery } from '@tanstack/react-query';
import {
    getAutomationExecutionsAPI,
    getAutomationHeldAPI,
    getAutomationLimitAPI,
    getAutomationRulesAPI,
    getGiftCandidatesAPI,
    getGiftChargePolicyAPI,
    getGiftChargesAPI,
    getGiftDetailAPI,
    getGiftHistoryAPI,
    getGiftPointAccountAPI,
    getGiftProductsAPI,
    getGiftSegmentsAPI,
    getGiftSettingsAPI,
    getGiftSummaryAPI,
    getGiftTopProductsAPI,
    getTrainerBudgetPermissionsAPI,
} from '../api/gifticon';
import { GiftHistoryQuery, Paging } from '../types/gifticon';

// 기프티콘 도메인 쿼리 키 — 무효화는 useGifticonMutationService에서 이 키 기준으로 수행
export const GIFTICON_KEYS = {
    all: ['gifticon'] as const,
    pointAccount: () => [...GIFTICON_KEYS.all, 'pointAccount'] as const,
    chargePolicy: () => [...GIFTICON_KEYS.all, 'chargePolicy'] as const,
    charges: (paging: Paging) => [...GIFTICON_KEYS.all, 'charges', paging] as const,
    segments: () => [...GIFTICON_KEYS.all, 'segments'] as const,
    candidates: (segments: string) => [...GIFTICON_KEYS.all, 'candidates', segments] as const,
    settings: () => [...GIFTICON_KEYS.all, 'settings'] as const,
    products: () => [...GIFTICON_KEYS.all, 'products'] as const,
    history: (query: GiftHistoryQuery) => [...GIFTICON_KEYS.all, 'history', query] as const,
    historyRoot: () => [...GIFTICON_KEYS.all, 'history'] as const,
    historyDetail: (batchId: string) => [...GIFTICON_KEYS.all, 'historyDetail', batchId] as const,
    automationRules: () => [...GIFTICON_KEYS.all, 'automationRules'] as const,
    automationLimit: () => [...GIFTICON_KEYS.all, 'automationLimit'] as const,
    automationHeld: () => [...GIFTICON_KEYS.all, 'automationHeld'] as const,
    automationExecutions: (ruleId: string, paging: Paging) =>
        [...GIFTICON_KEYS.all, 'automationExecutions', ruleId, paging] as const,
    budgetPermissions: () => [...GIFTICON_KEYS.all, 'budgetPermissions'] as const,
    summary: () => [...GIFTICON_KEYS.all, 'summary'] as const,
    topProducts: () => [...GIFTICON_KEYS.all, 'topProducts'] as const,
};

const getErrorStatus = (error: unknown): number | undefined => {
    const status = (error as { response?: { status?: number } } | null | undefined)?.response?.status;
    return typeof status === 'number' ? status : undefined;
};

// 4xx는 재시도 무의미(401/403/404/티어 검증 등). 5xx/네트워크만 1회 재시도.
const shouldRetry = (failureCount: number, error: unknown): boolean => {
    if (failureCount >= 1) return false;
    const status = getErrorStatus(error);
    if (status !== undefined && status >= 400 && status < 500) return false;
    return true;
};

const retryWithBackoff = (attemptIndex: number): number => Math.min(1000 * 2 ** attemptIndex, 8000);

interface BaseOptions {
    accessToken: string;
    enabled?: boolean;
    /**
     * 마운트할 때 항상 다시 받아 올지.
     *
     * 돈이 걸린 화면(발송 확인 창)은 stale 값을 그대로 보여 주면 안 된다 —
     * 서버는 발송 순간의 가격·잔액으로 차감한다.
     */
    refetchOnMount?: boolean | 'always';
}

const baseQueryOptions = {
    staleTime: 30 * 1000,
    retry: shouldRetry,
    retryDelay: retryWithBackoff,
};

export const useGiftPointAccountQuery = ({ accessToken, enabled = true, refetchOnMount }: BaseOptions) =>
    useQuery({
        queryKey: GIFTICON_KEYS.pointAccount(),
        queryFn: () => getGiftPointAccountAPI(accessToken),
        enabled: enabled && Boolean(accessToken),
        ...baseQueryOptions,
        ...(refetchOnMount === undefined ? {} : { refetchOnMount }),
    });

export const useGiftChargePolicyQuery = ({ accessToken, enabled = true }: BaseOptions) =>
    useQuery({
        queryKey: GIFTICON_KEYS.chargePolicy(),
        queryFn: () => getGiftChargePolicyAPI(accessToken),
        enabled: enabled && Boolean(accessToken),
        ...baseQueryOptions,
        staleTime: 5 * 60 * 1000, // 정책은 자주 안 바뀜
    });

export const useGiftChargesQuery = ({ accessToken, enabled = true, page, size }: BaseOptions & Paging) =>
    useQuery({
        queryKey: GIFTICON_KEYS.charges({ page, size }),
        queryFn: () => getGiftChargesAPI(accessToken, { page, size }),
        enabled: enabled && Boolean(accessToken),
        ...baseQueryOptions,
    });

export const useGiftSegmentsQuery = ({ accessToken, enabled = true }: BaseOptions) =>
    useQuery({
        queryKey: GIFTICON_KEYS.segments(),
        queryFn: () => getGiftSegmentsAPI(accessToken),
        enabled: enabled && Boolean(accessToken),
        ...baseQueryOptions,
    });

export const useGiftCandidatesQuery = ({
    accessToken,
    enabled = true,
    segments,
}: BaseOptions & { segments: string }) =>
    useQuery({
        queryKey: GIFTICON_KEYS.candidates(segments),
        queryFn: () => getGiftCandidatesAPI(accessToken, segments),
        enabled: enabled && Boolean(accessToken) && segments.length > 0,
        ...baseQueryOptions,
    });

export const useGiftSettingsQuery = ({ accessToken, enabled = true }: BaseOptions) =>
    useQuery({
        queryKey: GIFTICON_KEYS.settings(),
        queryFn: () => getGiftSettingsAPI(accessToken),
        enabled: enabled && Boolean(accessToken),
        ...baseQueryOptions,
    });

export const useGiftProductsQuery = ({ accessToken, enabled = true, refetchOnMount }: BaseOptions) =>
    useQuery({
        queryKey: GIFTICON_KEYS.products(),
        queryFn: () => getGiftProductsAPI(accessToken),
        enabled: enabled && Boolean(accessToken),
        ...baseQueryOptions,
        staleTime: 5 * 60 * 1000, // 카탈로그는 일 단위 동기화
        ...(refetchOnMount === undefined ? {} : { refetchOnMount }),
    });

export const useGiftHistoryQuery = ({ accessToken, enabled = true, query }: BaseOptions & { query: GiftHistoryQuery }) =>
    useQuery({
        queryKey: GIFTICON_KEYS.history(query),
        queryFn: () => getGiftHistoryAPI(accessToken, query),
        enabled: enabled && Boolean(accessToken),
        ...baseQueryOptions,
    });

export const useGiftDetailQuery = ({ accessToken, enabled = true, batchId }: BaseOptions & { batchId: string }) =>
    useQuery({
        queryKey: GIFTICON_KEYS.historyDetail(batchId),
        queryFn: () => getGiftDetailAPI(accessToken, batchId),
        enabled: enabled && Boolean(accessToken) && Boolean(batchId),
        ...baseQueryOptions,
    });

export const useAutomationRulesQuery = ({ accessToken, enabled = true }: BaseOptions) =>
    useQuery({
        queryKey: GIFTICON_KEYS.automationRules(),
        queryFn: () => getAutomationRulesAPI(accessToken),
        enabled: enabled && Boolean(accessToken),
        ...baseQueryOptions,
    });

export const useAutomationLimitQuery = ({ accessToken, enabled = true }: BaseOptions) =>
    useQuery({
        queryKey: GIFTICON_KEYS.automationLimit(),
        queryFn: () => getAutomationLimitAPI(accessToken),
        enabled: enabled && Boolean(accessToken),
        ...baseQueryOptions,
    });

export const useAutomationHeldQuery = ({ accessToken, enabled = true }: BaseOptions) =>
    useQuery({
        queryKey: GIFTICON_KEYS.automationHeld(),
        queryFn: () => getAutomationHeldAPI(accessToken),
        enabled: enabled && Boolean(accessToken),
        ...baseQueryOptions,
    });

export const useAutomationExecutionsQuery = ({
    accessToken,
    enabled = true,
    ruleId,
    page,
    size,
}: BaseOptions & { ruleId: string } & Paging) =>
    useQuery({
        queryKey: GIFTICON_KEYS.automationExecutions(ruleId, { page, size }),
        queryFn: () => getAutomationExecutionsAPI(accessToken, ruleId, { page, size }),
        enabled: enabled && Boolean(accessToken) && Boolean(ruleId),
        ...baseQueryOptions,
    });

export const useTrainerBudgetPermissionsQuery = ({ accessToken, enabled = true }: BaseOptions) =>
    useQuery({
        queryKey: GIFTICON_KEYS.budgetPermissions(),
        queryFn: () => getTrainerBudgetPermissionsAPI(accessToken),
        enabled: enabled && Boolean(accessToken),
        ...baseQueryOptions,
    });

export const useGiftSummaryQuery = ({ accessToken, enabled = true }: BaseOptions) =>
    useQuery({
        queryKey: GIFTICON_KEYS.summary(),
        queryFn: () => getGiftSummaryAPI(accessToken),
        enabled: enabled && Boolean(accessToken),
        ...baseQueryOptions,
    });

export const useGiftTopProductsQuery = ({ accessToken, enabled = true }: BaseOptions) =>
    useQuery({
        queryKey: GIFTICON_KEYS.topProducts(),
        queryFn: () => getGiftTopProductsAPI(accessToken),
        enabled: enabled && Boolean(accessToken),
        ...baseQueryOptions,
    });
